<script setup>
import { ref, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignsAPI from 'dashboard/api/campaigns';
import WhatsappCampaignsAPI from 'dashboard/api/whatsappCampaigns';

const props = defineProps({
  campaign: { type: Object, required: true },
  isOpen: { type: Boolean, default: false },
  filter: { type: String, default: 'all' },
  campaignType: {
    type: String,
    default: 'campaign',
    validator: value => ['campaign', 'whatsapp_campaign'].includes(value),
  },
});

const emit = defineEmits(['close']);

const replies = ref([]);
const summary = ref({ positive: 0, negative: 0, neutral: 0, total: 0 });
const isLoading = ref(false);
const currentPage = ref(1);
const totalPages = ref(0);
const totalCount = ref(0);
const activeFilter = ref(props.filter);

const fetchReplies = async (page = 1) => {
  isLoading.value = true;
  try {
    const api =
      props.campaignType === 'whatsapp_campaign'
        ? WhatsappCampaignsAPI
        : CampaignsAPI;
    const { data } = await api.getReplies(props.campaign.id, {
      page,
      per_page: 25,
      filter: activeFilter.value,
    });
    replies.value = data.replies;
    currentPage.value = data.meta.current_page;
    totalPages.value = data.meta.total_pages;
    totalCount.value = data.meta.total_count;
    if (data.summary) summary.value = data.summary;
  } catch {
    replies.value = [];
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.isOpen,
  open => {
    if (open) {
      activeFilter.value = props.filter;
      fetchReplies(1);
    }
  },
  { immediate: true }
);

const setFilter = f => {
  activeFilter.value = f;
  fetchReplies(1);
};

const sentimentBadge = sentiment => {
  const map = {
    positiva: { label: 'Positiva', bg: 'bg-n-teal-3', text: 'text-n-teal-11', icon: 'i-lucide-thumbs-up' },
    negativa: { label: 'Negativa', bg: 'bg-n-ruby-3', text: 'text-n-ruby-11', icon: 'i-lucide-thumbs-down' },
    neutra: { label: 'Neutra', bg: 'bg-n-slate-3', text: 'text-n-slate-11', icon: 'i-lucide-minus' },
  };
  return map[sentiment] || map.neutra;
};

const formatDate = date => {
  if (!date) return '-';
  return new Date(date).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const openConversation = reply => {
  const accountId = props.campaign.account_id;
  const url = `/app/accounts/${accountId}/conversations/${reply.display_id}`;
  window.open(url, '_blank');
};
</script>

<template>
  <teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-5"
      @click.self="emit('close')"
    >
      <div
        class="bg-n-solid-1 rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
        >
          <div>
            <h2 class="text-lg font-semibold text-n-slate-12">
              Respostas da Campanha
            </h2>
            <p class="text-sm text-n-slate-10 mt-0.5">
              {{ campaign.title }} — Análise por IA
            </p>
          </div>
          <Button
            icon="i-lucide-x"
            size="sm"
            variant="ghost"
            color="slate"
            @click="emit('close')"
          />
        </div>

        <!-- Summary + Filters -->
        <div class="flex items-center gap-3 px-6 py-3 border-b border-n-weak">
          <button
            class="px-3 py-1.5 text-xs rounded-md font-medium"
            :class="
              activeFilter === 'all'
                ? 'bg-n-blue-3 text-n-blue-11'
                : 'text-n-slate-10 hover:bg-n-alpha-1'
            "
            @click="setFilter('all')"
          >
            Todas ({{ summary.total }})
          </button>
          <button
            class="px-3 py-1.5 text-xs rounded-md font-medium flex items-center gap-1"
            :class="
              activeFilter === 'positive'
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'text-n-slate-10 hover:bg-n-alpha-1'
            "
            @click="setFilter('positive')"
          >
            <span class="i-lucide-thumbs-up text-xs" />
            Positivas ({{ summary.positive }})
          </button>
          <button
            class="px-3 py-1.5 text-xs rounded-md font-medium flex items-center gap-1"
            :class="
              activeFilter === 'negative'
                ? 'bg-n-ruby-3 text-n-ruby-11'
                : 'text-n-slate-10 hover:bg-n-alpha-1'
            "
            @click="setFilter('negative')"
          >
            <span class="i-lucide-thumbs-down text-xs" />
            Negativas ({{ summary.negative }})
          </button>
          <button
            class="px-3 py-1.5 text-xs rounded-md font-medium flex items-center gap-1"
            :class="
              activeFilter === 'neutral'
                ? 'bg-n-slate-3 text-n-slate-11'
                : 'text-n-slate-10 hover:bg-n-alpha-1'
            "
            @click="setFilter('neutral')"
          >
            <span class="i-lucide-minus text-xs" />
            Neutras ({{ summary.neutral }})
          </button>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-auto">
          <div
            v-if="isLoading"
            class="flex flex-col items-center justify-center py-10 gap-2"
          >
            <Spinner />
            <p class="text-sm text-n-slate-10">
              Analisando respostas com IA...
            </p>
          </div>

          <div
            v-else-if="replies.length === 0"
            class="flex flex-col items-center justify-center py-10 text-n-slate-10"
          >
            <span class="i-lucide-message-circle-off text-3xl mb-2" />
            <p>Nenhuma resposta nesta categoria</p>
          </div>

          <div v-else class="divide-y divide-n-weak">
            <div
              v-for="reply in replies"
              :key="reply.conversation_id"
              class="px-6 py-3 hover:bg-n-alpha-1 cursor-pointer flex items-start gap-4"
              @click="openConversation(reply)"
            >
              <!-- Avatar -->
              <div
                class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                :class="sentimentBadge(reply.sentiment).bg"
              >
                <span
                  class="text-sm"
                  :class="[
                    sentimentBadge(reply.sentiment).text,
                    sentimentBadge(reply.sentiment).icon,
                  ]"
                />
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="font-medium text-sm text-n-slate-12 truncate">
                    {{ reply.contact_name || 'Sem nome' }}
                  </span>
                  <span class="text-xs text-n-slate-9">
                    {{ reply.formatted_phone }}
                  </span>
                  <span
                    class="text-xs px-1.5 py-0.5 rounded font-medium"
                    :class="[
                      sentimentBadge(reply.sentiment).bg,
                      sentimentBadge(reply.sentiment).text,
                    ]"
                  >
                    {{ sentimentBadge(reply.sentiment).label }}
                  </span>
                </div>
                <p class="text-sm text-n-slate-11 mt-1">
                  "{{ reply.last_reply || '-' }}"
                </p>
                <p
                  v-if="reply.sentiment_reason"
                  class="text-xs text-n-slate-9 mt-0.5 italic"
                >
                  IA: {{ reply.sentiment_reason }}
                </p>
              </div>

              <!-- Date -->
              <div
                class="text-xs text-n-slate-9 whitespace-nowrap flex-shrink-0 mt-1"
              >
                {{ formatDate(reply.last_reply_at) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div
          v-if="totalPages > 1"
          class="flex items-center justify-between px-6 py-3 border-t border-n-weak"
        >
          <Button
            :disabled="currentPage <= 1"
            size="sm"
            variant="ghost"
            icon="i-lucide-chevron-left"
            @click="fetchReplies(currentPage - 1)"
          >
            Anterior
          </Button>
          <span class="text-xs text-n-slate-10">
            Página {{ currentPage }} de {{ totalPages }}
          </span>
          <Button
            :disabled="currentPage >= totalPages"
            size="sm"
            variant="ghost"
            icon="i-lucide-chevron-right"
            @click="fetchReplies(currentPage + 1)"
          >
            Próxima
          </Button>
        </div>
      </div>
    </div>
  </teleport>
</template>
